/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable max-statements */
import React, { useState, useRef, DependencyList, useEffect } from 'react';
import { Buffer } from 'buffer';

import ReactCrop, {
	centerCrop,
	makeAspectCrop,
	Crop,
	PixelCrop
} from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';
import { imgPreview } from './imgPreview';
import ParentModal from '../../../../Utils/Components/modals/parentModal';
import { CroppableImagePreview as FullScreenImageCrop } from './fullScreenImageCrop';
import { ImageCropRatios } from './imageCropRatio';
import { AspectRatiosEnum } from '../editImage';
import { useTypedSelector } from '../../../../../hooks/useTypeSelector';
import { useDispatch } from 'react-redux';
import {
	setNewStandardCrop,
	setOldStandardCrop,
	setPortraitCrop,
	setStandardCrop,
	setWideCrop
} from '../../../../../redux/actions/cropAction';
import { AddSingleImageModalContent } from '../addSingleImageModalContent';

function centerAspectCrop(
	mediaWidth: number,
	mediaHeight: number,
	aspect: number
) {
	return centerCrop(
		makeAspectCrop(
			{
				unit: '%',
				width: 100
			},
			aspect,
			mediaWidth,
			mediaHeight
		),
		mediaWidth,
		mediaHeight
	);
}

export default function CropImage({
	src,
	setUrl,
	setIsChanged,
	setCroppedFile,
	name,
	topicId,
	imageId
}: {
	src: string;
	setUrl: (url: string) => void;
	setIsChanged: (isChanged: boolean) => void;
	setCroppedFile: (file: File | undefined) => void;
	name: string;
	topicId: any;
	imageId: any;
}) {
	const previewCanvasRef = useRef<HTMLCanvasElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);
	const [crop, setCrop] = useState<Crop>();
	const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
	const [croppedImage, setCroppedImage] = useState<string>();
	const [isChangeImageModalOpen, setChangeImageModalOpen] = useState(false);
	const [isChangeImageModalChanged, setChangeImageModalChanged] =
		useState(false);

	const [isWarnModalOpen, setIsWarnModalOpen] = useState(false);

	const [downloadLinkSource, setDownloadLinkSource] = useState<string>(src);
	const [scale] = useState(1);
	const [rotate] = useState(0);
	const [aspect] = useState<number | undefined>(16 / 9);
	const [hasError, setHasError] = useState(false);

	const [isImageModalPreviewOpen, setIsImageModalPreviewOpen] = useState(false);
	const [imageModalPreviewAspectRatio, setImageModalPreviewAspectRatio] =
		useState('aspect-[16/10]');

	// Original image
	const [image, setImage] = useState<HTMLImageElement>();
	const [previewImageSrc, setPreviewImageSrc] = useState(src);

	// Crop Image preview
	const [oldstandardPreviewImageSrc] = useState<string>(src);
	const [newStandardPreviewImageSrc] = useState<string>(src);
	const [widePreviewImageSrc] = useState<string>(src);
	const [portraitPreviewImageSrc] = useState<string>(src);

	function downloadURI() {
		const downloadLink = document.createElement('a');
		document.body.appendChild(downloadLink);
		downloadLink.href = downloadLinkSource;
		downloadLink.target = '_self';
		downloadLink.download = name + '.jpeg';
		downloadLink.click();
	}

	const { selectedCropARType, standardCrop } = useTypedSelector(
		(state) => state.crop
	);

	useEffect(() => {
		if (croppedImage != null) setDownloadLinkSource(croppedImage);
		else setDownloadLinkSource(src);
	}, [croppedImage]);

	function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
		const image = e.currentTarget;
		if (aspect) {
			const { width, height } = image;
			if (standardCrop != null)
				try {
					setCrop(standardCrop);
				} catch (e) {
					setCrop(centerAspectCrop(width, height, aspect));
				}
			else {
				setCrop(undefined);
				setImage(undefined);
				setCroppedFile(undefined);
				setCroppedImage(undefined);
			}
		}
	}

	function useDebounceEffect(
		fn: (deps: DependencyList) => void,
		waitTime: number,
		deps: DependencyList
	) {
		useEffect(() => {
			const t = setTimeout(() => {
				fn.apply(undefined, [deps]);
			}, waitTime);

			return () => {
				clearTimeout(t);
			};
		}, deps);
	}

	useDebounceEffect(
		async () => {
			if (
				completedCrop?.width &&
				completedCrop?.height &&
				imgRef.current &&
				previewCanvasRef.current
			) {
				dispatch(setStandardCrop(completedCrop));

				imgPreview(
					imgRef.current,
					setCroppedImage,
					completedCrop,
					scale,
					rotate
				);
			}
		},
		100,
		[completedCrop, scale, rotate]
	);

	function dataURLtoBlob(dataUrl: string, filename: string) {
		const arr = dataUrl.split(',');
		if (arr.length < 2) {
			return undefined;
		}
		const mimeArr = arr[0].match(/:(.*?);/);
		if (!mimeArr || mimeArr.length < 2) {
			return undefined;
		}
		const mime = mimeArr[1];
		const buff = Buffer.from(arr[1], 'base64');
		return new File([buff], filename, { type: mime });
	}

	const dispatch = useDispatch();

	useEffect(() => {
		if (croppedImage != null) {
			const file = dataURLtoBlob(croppedImage, name + '.jpeg');
			if (file != null) setCroppedFile(file);
			const image = new Image();
			image.src = croppedImage;
			setImage(image);
		}
	}, [croppedImage]);

	const clearCrop = () => {
		setCrop(undefined);
		setImage(undefined);
		setCroppedFile(undefined);
		setCroppedImage(undefined);

		dispatch(setStandardCrop(null));
		dispatch(setNewStandardCrop(null));
		dispatch(setOldStandardCrop(null));
		dispatch(setPortraitCrop(null));
		dispatch(setWideCrop(null));
		setIsChanged(true);
	};

	return (
		<>
			<ParentModal
				visible={isImageModalPreviewOpen}
				setModalOpen={setIsImageModalPreviewOpen}
				isChanged={false}
				setIsChanged={() => {
					return;
				}}
				isWarnModalOpen={false}
				setIsWarnModalOpen={() => {
					return;
				}}
			>
				<FullScreenImageCrop
					originalImage={
						AspectRatiosEnum.NEW_STANDARD
							? newStandardPreviewImageSrc
							: selectedCropARType === AspectRatiosEnum.WIDE
							? widePreviewImageSrc
							: selectedCropARType === AspectRatiosEnum.PORTRAIT
							? portraitPreviewImageSrc
							: selectedCropARType === AspectRatiosEnum.OLD_STANDARD
							? oldstandardPreviewImageSrc
							: previewImageSrc
					}
					setIsImageModalPreviewOpen={setIsImageModalPreviewOpen}
					setIsChanged={setIsChanged}
				/>
			</ParentModal>

			<ParentModal
				visible={isChangeImageModalOpen}
				setModalOpen={setChangeImageModalOpen}
				isChanged={isChangeImageModalChanged}
				setIsChanged={setChangeImageModalChanged}
				isWarnModalOpen={isWarnModalOpen}
				setIsWarnModalOpen={setIsWarnModalOpen}
			>
				<AddSingleImageModalContent
					setModalOpen={setChangeImageModalOpen}
					topicId={topicId}
					imageId={imageId}
					topicName={name}
					isChanged={isChangeImageModalChanged}
					setIsChanged={setChangeImageModalChanged}
					setIsWarnModalOpen={setIsWarnModalOpen}
					setUrl={setUrl}
					clearCrop={clearCrop}
				/>
			</ParentModal>
			<div className="border-t mt-8 border-gray-300 dark:border-gray-700 transition-all duration-500 text-center w-8/12 mx-auto" />
			<div className="text-center mt-5">
				<div className="flex flex-row w-8/12 mx-auto">
					<button
						className="btn btn-primary btn-sm"
						onClick={() => {
							downloadURI();
						}}
					>
						Download preview
					</button>

					<button
						className="btn btn-primary btn-sm ml-2"
						onClick={() => {
							setChangeImageModalOpen(true);
						}}
					>
						Change Image
					</button>

					{crop != null && (
						<button
							className="btn btn-primary btn-sm ml-auto"
							onClick={clearCrop}
						>
							clear crop
						</button>
					)}
				</div>
				<div className="mx-auto w-full flex justify-center my-3">
					<div className="w-8/12 flex flex-col space-y-3">
						<ReactCrop
							crop={crop}
							onChange={(_, percentCrop) => {
								setIsChanged(true);
								setCrop(percentCrop);
							}}
							onComplete={(c) => {
								setCompletedCrop(c);
								setCrop(c);
							}}
							aspect={aspect}
						>
							<img
								ref={imgRef}
								alt="Crop me"
								className="rounded-lg block object-cover object-center aspect-[16/9] mx-auto"
								src={src}
								style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
								onLoad={onImageLoad}
								crossOrigin={hasError ? undefined : 'anonymous'}
								onError={() => setHasError(true)}
							/>
						</ReactCrop>

						<ImageCropRatios
							completedCrop={completedCrop}
							image={image}
							previewCanvasRef={previewCanvasRef}
							setImageModalPreviewAspectRatio={setImageModalPreviewAspectRatio}
							setIsImageModalPreviewOpen={setIsImageModalPreviewOpen}
							setPreviewImageSrc={setPreviewImageSrc}
							src={src}
						/>
					</div>
				</div>
			</div>
		</>
	);
}
